import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  User, LogOut, Edit, Eye, Menu, X, Home, Users, 
  GraduationCap, Bell, Calendar, BookOpen, MessageSquare,
  CheckCircle, XCircle, Building2, Star, Clock, Megaphone, Share2,
  Plus, Briefcase, Trash2, Copy
} from "lucide-react";
import { handleShare as safeHandleShare, getProfileShareUrlAsync, safeCopyToClipboard } from "@/utils/shareHelper";
import WhatsAppSupport from "@/components/WhatsAppSupport";
import ProfileCompleteness from "@/components/ProfileCompleteness";
import NotificationBell from "@/components/NotificationBell";
import EventsSection from "@/components/EventsSection";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

export default function TrainerDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [linkRequests, setLinkRequests] = useState([]);
  const [managedBy, setManagedBy] = useState([]);
  const [myAnnouncements, setMyAnnouncements] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({ title: "", content: "", announcement_type: "general" });
  const [shareModal, setShareModal] = useState({ open: false, url: "" });

  /**
   * Share handler - MUST be called directly in onClick (user gesture)
   * Opens native share sheet on mobile (WhatsApp, etc.)
   * Falls back to copy link if share unavailable
   */
  const handleShare = async () => {
    const shareUrl = await getProfileShareUrlAsync(profile, "trainer");
    await safeHandleShare({
      url: shareUrl,
      title: `${profile?.personal_info?.full_name || "Trainer"} on TalentMeet`,
      text: `Check out ${profile?.personal_info?.full_name || "this trainer"}'s profile on TalentMeet!`,
      onShowModal: (url) => setShareModal({ open: true, url })
    });
  };

  // Copy from modal fallback
  const copyShareUrl = async () => {
    const success = await safeCopyToClipboard(shareModal.url);
    if (success) {
      toast.success("Link copied!");
      setShareModal({ open: false, url: "" });
    } else {
      toast.info("Long press the link to copy");
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      // Verify role is trainer
      if (parsed.role !== "trainer") {
        toast.error("Access denied. Redirecting to your dashboard.");
        window.location.href = `/dashboard/${parsed.role}`;
        return;
      }
      setUser(parsed);
    }
    fetchDashboardData();
    fetchMyAnnouncements();
    fetchOpportunities();
  }, []); // Remove navigate from dependencies

  const fetchMyAnnouncements = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/announcements/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyAnnouncements(data.announcements || []);
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  };

  const fetchOpportunities = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/opportunities?status=open&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOpportunities(data.opportunities || []);
      }
    } catch (error) {
      console.error("Error fetching opportunities:", error);
    }
  };

  const createAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.content) {
      toast.error("Please fill in title and content");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/announcements`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(announcementForm)
      });
      if (response.ok) {
        toast.success("Announcement created!");
        setShowAnnouncementForm(false);
        setAnnouncementForm({ title: "", content: "", announcement_type: "general" });
        fetchMyAnnouncements();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to create announcement");
      }
    } catch (error) {
      toast.error("Failed to create announcement");
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/announcements/${announcementId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Announcement deleted");
        fetchMyAnnouncements();
      }
    } catch (error) {
      toast.error("Failed to delete announcement");
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/dashboard/trainer/data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 403) {
        const err = await response.json();
        toast.error(err.detail || "Access denied");
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        window.location.href = `/dashboard/${storedUser.role || "talent"}`;
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
        setProfile(data.profile);
        setLinkRequests(data.link_requests || []);
        setManagedBy(data.managed_by || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespondToLink = async (linkId, action, relationshipType = null) => {
    try {
      const token = localStorage.getItem("token");
      const body = { action };
      if (relationshipType) body.relationship_type = relationshipType;
      
      const response = await fetch(`${API_URL}/api/links/${linkId}/respond`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success(action === "approve" ? "Request approved!" : "Request declined");
        fetchDashboardData();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to respond");
      }
    } catch (error) {
      toast.error("Failed to respond to request");
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const offerings = dashboardData?.offerings || [];
  const stats = { 
    ...dashboardData?.stats || {},
    announcements: myAnnouncements.length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg object-cover" />
              <span className="font-display text-lg font-bold text-gray-900 hidden sm:block">TalentMeet</span>
              <Badge className="bg-orange-100 text-orange-700 ml-2">Trainer</Badge>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              <Link to="/dashboard/trainer" className="px-3 py-2 text-sm font-medium text-orange-600 bg-orange-50 rounded-lg">
                Dashboard
              </Link>
              <Link to="/browse" className="px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg">
                Browse Talents
              </Link>
              <Link to="/chat" className="px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg relative">
                Messages
              </Link>
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-2">
              <NotificationBell />
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" className="text-gray-500">
                <LogOut className="w-5 h-5" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 p-3">
            <nav className="space-y-1">
              <Link to="/dashboard/trainer" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-orange-50 text-orange-600">
                <Home className="w-5 h-5" /> Dashboard
              </Link>
              <Link to="/browse" className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
                <Users className="w-5 h-5" /> Browse Talents
              </Link>
              <Link to="/chat" className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
                <MessageSquare className="w-5 h-5" /> Messages
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-5 mb-6 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold mb-1">Welcome, {user?.name?.split(" ")[0]}!</h1>
              <p className="text-white/80 text-sm">
                Manage your classes and connect with students
              </p>
            </div>
            <div className="flex items-center gap-2">
              <GraduationCap className="w-6 h-6" />
              <span className="font-medium">Trainer Dashboard</span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-orange-50 border-orange-100">
            <CardContent className="p-4 text-center">
              <BookOpen className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-orange-700">{stats.total_offerings || 0}</p>
              <p className="text-xs text-gray-600">Offerings</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-100">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-700">{stats.enrollments || 0}</p>
              <p className="text-xs text-gray-600">Enrollments</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-100">
            <CardContent className="p-4 text-center">
              <Megaphone className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-700">{stats.announcements || 0}</p>
              <p className="text-xs text-gray-600">Announcements</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-100">
            <CardContent className="p-4 text-center">
              <Building2 className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-700">{managedBy.length}</p>
              <p className="text-xs text-gray-600">Affiliations</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Your Trainer Profile</CardTitle>
                  {profile?.is_draft && <Badge variant="outline" className="text-xs">Draft</Badge>}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-4">
                  {profile?.portfolio?.profile_photo ? (
                    <img 
                      src={profile.portfolio.profile_photo} 
                      alt="Profile"
                      className="w-20 h-24 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-20 h-24 rounded-xl bg-gradient-to-br from-orange-400 to-amber-400 flex items-center justify-center">
                      <GraduationCap className="w-8 h-8 text-white/80" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900">
                      {profile?.personal_info?.display_name || profile?.personal_info?.full_name || "Your Name"}
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">
                      {profile?.trainer_info?.expertise_domain || profile?.professional_info?.primary_category || "Trainer"}
                    </p>
                    
                    {/* Profile Completion */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-500">Profile Completion</span>
                        <span className="font-medium text-orange-600">{profile?.completion_percentage || 0}%</span>
                      </div>
                      <Progress value={profile?.completion_percentage || 0} className="h-1.5" />
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => navigate("/trainer/wizard")}
                        className="bg-orange-600 hover:bg-orange-700"
                        data-testid="edit-profile-btn"
                      >
                        <Edit className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => navigate(`/profile/${profile?.profile_id}`)}
                        data-testid="view-profile-btn"
                      >
                        <Eye className="w-3 h-3 mr-1" /> View
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={handleShare}
                        data-testid="share-profile-btn"
                        title="Copy shareable profile link"
                      >
                        <Share2 className="w-3 h-3 mr-1" /> Share
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Offerings Section */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-orange-600" />
                    My Offerings / Classes
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={() => navigate("/trainer/wizard")}>
                    + Add Offering
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {offerings.length > 0 ? (
                  <div className="space-y-3">
                    {offerings.map((offering, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{offering.title}</h4>
                            <p className="text-sm text-gray-500">{offering.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span><Clock className="w-3 h-3 inline mr-1" />{offering.duration}</span>
                              <span className="font-medium text-orange-600">₹{offering.price}</span>
                              <Badge variant="secondary" className="text-xs">{offering.mode}</Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No offerings yet. Add your classes and workshops!</p>
                    <Button 
                      size="sm" 
                      className="mt-3 bg-orange-600 hover:bg-orange-700"
                      onClick={() => navigate("/trainer/wizard")}
                    >
                      Add First Offering
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Profile Completeness */}
            <ProfileCompleteness profile={profile} />

            {/* Events Section */}
            <EventsSection userRole="trainer" title="Events" />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Collaboration Requests */}
            {linkRequests.length > 0 && (
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-orange-600" />
                    Affiliation Requests
                    <Badge className="ml-auto bg-orange-600 text-white">{linkRequests.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {linkRequests.slice(0, 3).map(req => (
                    <div key={req.link_id} className="p-3 bg-white rounded-lg border border-orange-100">
                      <p className="font-medium text-gray-900 text-sm">{req.enterprise_name}</p>
                      {req.message && <p className="text-xs text-gray-500 mt-1">{req.message}</p>}
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm" 
                          className="flex-1 bg-orange-600 hover:bg-orange-700 h-8 text-xs"
                          onClick={() => handleRespondToLink(req.link_id, "approve", "trainer_at")}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" /> Accept
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1 h-8 text-xs"
                          onClick={() => handleRespondToLink(req.link_id, "reject")}
                        >
                          <XCircle className="w-3 h-3 mr-1" /> Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Managed By */}
            {managedBy.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    Affiliated With
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {managedBy.map(link => (
                    <div key={link.link_id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{link.enterprise_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{link.relationship_type?.replace(/_/g, " ")}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Upcoming Schedule */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Upcoming Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Acting Workshop</p>
                      <p className="text-xs text-gray-500">Tomorrow, 6:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Voice Training Batch</p>
                      <p className="text-xs text-gray-500">Jan 8, 4:00 PM</p>
                    </div>
                  </div>
                </div>
                <Button variant="outline" className="w-full mt-4" size="sm">
                  <Calendar className="w-4 h-4 mr-2" /> View Schedule
                </Button>
              </CardContent>
            </Card>

            {/* Announcements */}
            <Card className="bg-gradient-to-br from-green-500 to-teal-500 text-white border-0">
              <CardContent className="p-4">
                <Megaphone className="w-8 h-8 mb-2" />
                <h3 className="font-semibold mb-1">Announcements</h3>
                <p className="text-sm text-white/80 mb-3">Post updates to your students and followers</p>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="w-full bg-white text-green-600 hover:bg-gray-100"
                  onClick={() => setShowAnnouncementForm(true)}
                  data-testid="create-announcement-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Announcement
                </Button>
              </CardContent>
            </Card>

            {/* Browse Opportunities */}
            <Card className="bg-gradient-to-br from-purple-500 to-indigo-500 text-white border-0">
              <CardContent className="p-4">
                <Briefcase className="w-8 h-8 mb-2" />
                <h3 className="font-semibold mb-1">Opportunities</h3>
                <p className="text-sm text-white/80 mb-3">Discover casting calls and projects</p>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="w-full bg-white text-purple-600 hover:bg-gray-100"
                  onClick={() => navigate("/browse")}
                  data-testid="browse-opportunities-btn"
                >
                  <Eye className="w-4 h-4 mr-2" /> Browse Opportunities
                </Button>
              </CardContent>
            </Card>

            {/* My Announcements List */}
            {myAnnouncements.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-green-600" />
                    My Announcements
                    <Badge variant="secondary" className="ml-auto">{myAnnouncements.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-64 overflow-y-auto">
                  {myAnnouncements.slice(0, 5).map(ann => (
                    <div key={ann.announcement_id} className="p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">{ann.title}</p>
                          <p className="text-xs text-gray-500 line-clamp-2 mt-1">{ann.content}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(ann.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => deleteAnnouncement(ann.announcement_id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Available Opportunities */}
            {opportunities.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-purple-600" />
                    Open Opportunities
                    <Badge variant="secondary" className="ml-auto">{opportunities.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-64 overflow-y-auto">
                  {opportunities.slice(0, 5).map(opp => (
                    <div key={opp.opportunity_id} className="p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 cursor-pointer">
                      <p className="font-medium text-sm text-gray-900">{opp.title}</p>
                      <p className="text-xs text-gray-500">{opp.company_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{opp.opportunity_type}</Badge>
                        {opp.location && <span className="text-xs text-gray-400">{opp.location}</span>}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Create Announcement Modal */}
      {showAnnouncementForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-green-600" />
                Create Announcement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input 
                  placeholder="Announcement title"
                  value={announcementForm.title}
                  onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                  data-testid="announcement-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Content</Label>
                <Textarea 
                  placeholder="Write your announcement..."
                  rows={4}
                  value={announcementForm.content}
                  onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})}
                  data-testid="announcement-content-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <select 
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={announcementForm.announcement_type}
                  onChange={(e) => setAnnouncementForm({...announcementForm, announcement_type: e.target.value})}
                >
                  <option value="general">General</option>
                  <option value="workshop">Workshop</option>
                  <option value="event">Event</option>
                  <option value="hiring">Hiring</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowAnnouncementForm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={createAnnouncement}
                  data-testid="submit-announcement-btn"
                >
                  Publish
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Share Link Modal - Fallback when native share unavailable */}
      <Dialog open={shareModal.open} onOpenChange={(open) => setShareModal({ ...shareModal, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-orange-600" />
              Share Your Profile
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Copy this link to share on WhatsApp, Instagram, or anywhere:
            </p>
            <div className="flex items-center gap-2">
              <Input 
                value={shareModal.url} 
                readOnly 
                className="text-sm font-mono bg-gray-50"
                onClick={(e) => e.target.select()}
                onFocus={(e) => e.target.select()}
              />
              <Button onClick={copyShareUrl} size="sm" className="shrink-0">
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
            </div>
            <p className="text-xs text-gray-500 text-center">
              Tap the link to select, then tap Copy button
            </p>
          </div>
        </DialogContent>
      </Dialog>
      
      <WhatsAppSupport />
    </div>
  );
}
